import type { FormEvent } from 'react';
import type React from 'react';

import {
  Box,
  FormField,
  FormHelp,
  Heading,
  Input,
  Label,
  Paragraph,
  Textarea,
  TextButton,
} from '@zillow/constellation-10';

import type Field from '../../../../models/Field';
import { PERSON } from '../../../../constants/index';
import { FULLSTORY_EXCLUDE } from '../../../../constants/analytics';
import type { Application } from '../../../../models/Types';
import PhoneInput from '../../../Widgets/PhoneInput';

type Props = {
  index: number;
  onFieldChange: (event: FormEvent<HTMLElement>) => void;
  onRemovePerson: (event: FormEvent<HTMLElement>) => void;
  firstNameField: Field;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  firstNameValue: any;
  lastNameField: Field;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  lastNameValue: any;
  phoneNumberField: Field;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  phoneNumberValue: any;
  emailField: Field;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  emailValue: any;
  mailingAddressField?: Field;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mailingAddressValue?: any;
  personTypeLabel: string;
  application?: Application;
  groupEndIndex?: number;
};

export default function PersonAndApplicantInformation({
  index,
  firstNameField,
  firstNameValue,
  lastNameField,
  lastNameValue,
  phoneNumberField,
  phoneNumberValue,
  emailField,
  emailValue,
  mailingAddressField,
  mailingAddressValue,
  onFieldChange,
  onRemovePerson,
  personTypeLabel,
  application,
  groupEndIndex,
}: Props): JSX.Element {
  function renderHeader() {
    return (
      <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
        <Paragraph textStyle="body-bold">
          {groupEndIndex && groupEndIndex !== index + 1
            ? `Tenants #${index + 1}–${groupEndIndex}`
            : `Tenant #${index + 1}`}
        </Paragraph>
        <TextButton data-test="remove-person" onClick={onRemovePerson}>
          Delete
        </TextButton>
      </Box>
    );
  }

  function renderPerson(): React.ReactNode {
    const hasHeader = !application || index === 0 || index > application.applicants.length - 1;
    const isReadOnly = application && index <= application.applicants.length - 1;

    return (
      <>
        {hasHeader && renderHeader()}
        {!hasHeader && <Box css={{ marginTop: 'layout.tight' }} />}
        {isReadOnly ? renderUneditableFields() : renderFields()}
      </>
    );
  }

  function renderUneditableFields(): React.ReactNode {
    return (
      <>
        <Heading level={4} textStyle="responsive.heading-sm">{`${firstNameValue} ${lastNameValue}`}</Heading>
        <Paragraph>{phoneNumberField ? `${emailValue} · ${phoneNumberValue}` : `${emailValue}`}</Paragraph>
        {mailingAddressField && (
          <FormField
            required
            css={{ marginTop: 'layout.tight' }}
            error={mailingAddressField.showOnError(PERSON.ERROR_MESSAGE.MAILING_ADDRESS) ? true : false}
            label={<Label>Address for receiving notices prior to the lease’s start date</Label>}
            control={
              <Textarea
                data-field-name={mailingAddressField.fieldName}
                value={mailingAddressValue}
                onChange={onFieldChange}
                css={{ resize: 'vertical' }}
                rows={3}
                autoComplete="street-address"
              />
            }
            description={
              mailingAddressField.showOnError(PERSON.ERROR_MESSAGE.MAILING_ADDRESS) && (
                <FormHelp>{mailingAddressField.showOnError(PERSON.ERROR_MESSAGE.MAILING_ADDRESS)}</FormHelp>
              )
            }
          />
        )}
      </>
    );
  }

  function renderFields(): React.ReactNode {
    return (
      <>
        <Box
          css={{
            display: 'grid',
            gridTemplateColumns: 'repeat(12, 1fr)',
            marginBottom: 'layout.tight',
            gap: 'layout.tight',
          }}
        >
          <Box css={{ gridColumnEnd: { base: 'span 6', md: 'span 12' } }}>
            <FormField
              required
              label={<Label>{PERSON.LABEL.FIRST_NAME}</Label>}
              control={
                <Input
                  data-field-name={firstNameField.fieldName}
                  type="fname"
                  value={firstNameValue}
                  onChange={onFieldChange}
                  autoComplete="given-name"
                />
              }
            />
          </Box>
          <Box css={{ gridColumnEnd: { base: 'span 6', md: 'span 12' } }}>
            <FormField
              required
              label={<Label id={`${personTypeLabel}#${index + 1}-last-name`}>{PERSON.LABEL.LAST_NAME}</Label>}
              control={
                <Input
                  aria-labelledby={`${personTypeLabel}#${index + 1}-last-name`}
                  data-field-name={lastNameField.fieldName}
                  type="lname"
                  value={lastNameValue}
                  onChange={onFieldChange}
                  autoComplete="family-name"
                />
              }
            />
          </Box>
        </Box>
        {mailingAddressField && (
          <FormField
            required
            css={{ marginBottom: 'layout.tight' }}
            error={mailingAddressField.showOnError(PERSON.ERROR_MESSAGE.MAILING_ADDRESS) ? true : false}
            label={<Label>Address for receiving notices prior to the lease’s start date</Label>}
            control={
              <Textarea
                data-field-name={mailingAddressField.fieldName}
                value={mailingAddressValue}
                onChange={onFieldChange}
                css={{ resize: 'vertical' }}
                rows={3}
                autoComplete="street-address"
              />
            }
            description={
              mailingAddressField.showOnError(PERSON.ERROR_MESSAGE.MAILING_ADDRESS) && (
                <FormHelp>{mailingAddressField.showOnError(PERSON.ERROR_MESSAGE.MAILING_ADDRESS)}</FormHelp>
              )
            }
          />
        )}
        <FormField
          required
          css={{ marginBottom: phoneNumberField ? 'sm' : 0 }}
          label={<Label>{PERSON.LABEL.EMAIL}</Label>}
          error={
            emailField.showOnError(
              emailValue.includes(' ') ? PERSON.ERROR_MESSAGE.EMAIL_ADDRESS_SPACE : PERSON.ERROR_MESSAGE.EMAIL_ADDRESS,
            )
              ? true
              : false
          }
          control={
            <Input
              data-field-name={emailField.fieldName}
              value={emailValue}
              onChange={onFieldChange}
              // type='email' Disabled due to inconsistent/unpredicatable support across browsers
              autoComplete="email"
            />
          }
          description={
            emailField.showOnError(
              emailValue.includes(' ') ? PERSON.ERROR_MESSAGE.EMAIL_ADDRESS_SPACE : PERSON.ERROR_MESSAGE.EMAIL_ADDRESS,
            ) && (
              <FormHelp>
                {emailField.showOnError(
                  emailValue.includes(' ')
                    ? PERSON.ERROR_MESSAGE.EMAIL_ADDRESS_SPACE
                    : PERSON.ERROR_MESSAGE.EMAIL_ADDRESS,
                )}
              </FormHelp>
            )
          }
        />
        {phoneNumberField && (
          <FormField
            required
            error={phoneNumberField.hasError}
            label={<Label>{PERSON.LABEL.PHONE_NUMBER}</Label>}
            control={
              <PhoneInput
                data-field-name={phoneNumberField.fieldName}
                value={phoneNumberValue}
                onChange={onFieldChange}
              />
            }
            description={phoneNumberField.hasError && <FormHelp>{phoneNumberField.getErrorMessage()}</FormHelp>}
          />
        )}
      </>
    );
  }

  return (
    <div key={index} className={FULLSTORY_EXCLUDE}>
      {renderPerson()}
    </div>
  );
}
